import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Formik, Form } from 'formik'
import * as Yup from 'yup'
import { Box, Typography, IconButton, Icon } from '@mui/material'

// Local Imports
import AdxTabsScrollable from 'components/reusable/tab/AdxTabsScrollable'
import AdxTabsPanel from 'components/reusable/tab/AdxTabsPanel'
import createLeaveTabs from '../../components/createLeaveTabs'
import { formikInitialValue } from '../../resource/policyObjects'

// API services
import { usePostNewLeavePolicy, usePutLeavePolicy } from 'services/api/modules/leave/leavePolicy/'
import AdxButton from 'components/custom/AdxButton'

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required').max(50, 'Maximum 50 characters only'),
  description: Yup.string().optional().max(120, 'Maximum 120 characters only'),
  attachment_required: Yup.boolean(),
  reason_required: Yup.boolean(),
  credit_deduction: Yup.boolean(),
  half_day_option: Yup.boolean(),
  include_non_working_days: Yup.boolean(),
  cycle_period: Yup.string()
    .required('Please select a cycle period')
    .oneOf(['monthly', 'yearly'], 'Please select from one of the values shown'),
  eligible_amount: Yup.number()
    .required('Required')
    .min(0, (min) => `Value must be greater than ${min.min} `),
  eligible_period: Yup.string().when('eligible_amount', {
    is: (val) => val > 0,
    then: (schema) => schema.required('Select a period'),
    otherwise: (schema) => schema.optional(),
  }),
  entitlement: Yup.array()
    .min(1, 'Must have at least one layer')
    .of(
      Yup.object().shape({
        layer: Yup.number(),
        amount: Yup.number()
          .required('Amount is required')
          .moreThan(0, ({ more }) => `Amount must be greater than ${more}`),
      })
    ),
  category: Yup.object().shape({
    gender: Yup.array()
      .of(
        Yup.object().shape({
          data: Yup.number(),
          status: Yup.number().oneOf([1, 2], 'Value outside range'),
        })
      )
      .compact((v) => v.status === 2 || v.status === 0)
      .min(1, 'At least one value must be selected'),
    marriage_status: Yup.array()
      .of(
        Yup.object().shape({
          data: Yup.number(),
          status: Yup.number().oneOf([1, 2], 'Value outside range'),
        })
      )
      .compact((v) => v.status === 2 || v.status === 0)
      .min(1, 'At least one value must be selected'),
    employment_type: Yup.array()
      .of(
        Yup.object().shape({
          data: Yup.number(),
          status: Yup.number().oneOf([1, 2], 'Value outside range'),
        })
      )
      .compact((v) => v.status === 2 || v.status === 0)
      .min(1, 'At least one value must be selected'),
  }),
  accrual_type: Yup.mixed()
    .required('Please select an accrual option')
    .oneOf([1, 2, 3], 'Please select from one of the values shown'),
  accrual_happen: Yup.string()
    .nullable()
    .when('accrual_type', {
      is: (val) => val === 3,
      then: (schema) =>
        schema.required('Select accrual period').oneOf(['start_month', 'end_month']),
      otherwise: (schema) => schema.optional(),
    }),
  approval_route_id: Yup.number().required('Select one of the approval layer'),
  quota_amount: Yup.number()
    .nullable()
    .when('checkboxes.quota', {
      is: (val) => val === true,
      then: (schema) => schema.required('Required'),
      otherwise: (schema) => schema.optional(),
    }),
  quota_unit: Yup.string()
    .nullable()
    .when('checkboxes.quota', {
      is: (val) => val === true,
      then: (schema) => schema.required('Required').oneOf(['percent', 'number'], 'required'),
      otherwise: (schema) => schema.optional(),
    }),
  quota_category: Yup.string()
    .nullable()
    .when('checkboxes.quota', {
      is: (val) => val === true,
      then: (schema) => schema.required('Required').oneOf(['department', 'company'], 'required'),
      otherwise: (schema) => schema.optional(),
    }),
  restriction_amount: Yup.number()
    .nullable()
    .when('checkboxes.restriction', {
      is: (val) => val === true,
      then: (schema) =>
        schema
          .required('Required')
          .moreThan(0, ({ more }) => `The restriction must be greater than ${more}`),
      otherwise: (schema) => schema.optional(),
    }),
  day_prior: Yup.number()
    .nullable()
    .when('checkboxes.minDate', {
      is: (val) => val === true,
      then: (schema) =>
        schema
          .required('Required')
          .moreThan(0, ({ more }) => `The restriction must be greater than ${more}`),
      otherwise: (schema) => schema.optional(),
    }),
  carry_forward_amount: Yup.number()
    .nullable()
    .when('checkboxes.carryForward', {
      is: (val) => val === true,
      then: (schema) =>
        schema.required('Required').when('carry_forward_amount', {
          is: (v) => v === 0,
          then: (schema) => schema.moreThan(0, ({ more }) => `must be more than ${more}`),
          otherwise: (schema) => schema,
        }),
      otherwise: (schema) => schema.optional(),
    }),
  carry_forward_expiry_amount: Yup.number()
    .nullable()
    .when(['checkboxes.carryForward', 'carry_forward_expiry_period'], {
      is: (checkbox, expiryPeriod) =>
        checkbox === true && expiryPeriod !== 'no_expiry' && expiryPeriod !== 'cycle_period',
      then: (schema) =>
        schema
          .required('Amount is required')
          .moreThan(0, ({ more }) => `must be more than ${more}`),
      otherwise: (schema) => schema.optional(),
    }),
  carry_forward_expiry_period: Yup.string()
    .nullable()
    .when('checkboxes.carryForward', {
      is: (val) => val === true,
      then: (schema) => schema.required('Please select the period'),
      otherwise: (schema) => schema.optional(),
    }),
  deduct_from_leave_policy_id: Yup.number()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .when('checkboxes.deductFromPolicy', {
      is: (val) => val === true,
      then: (schema) => schema.required('Select a leave policy'),
      otherwise: (schema) => schema.optional(),
    }),
})

function CreateLeavePolicyEntitlement({ editMode, policy }) {
  const navigate = useNavigate()
  const mutation = usePostNewLeavePolicy()
  const mutationEdit = usePutLeavePolicy()

  const editModeInitialValues = () => ({
    name: policy.name || '',
    description: policy.description || '',
    attachment_required: policy.attachment_required === 1 ? true : false,
    reason_required: policy.reason_required === 1 ? true : false,
    credit_deduction: policy.credit_deduction === 1 ? true : false,
    half_day_option: policy.half_day_option === 1 ? true : false,
    include_non_working_days: policy.include_non_working_days === 1 ? true : false,
    cycle_period: policy.cycle_period || '',
    eligible_amount: policy.eligible_amount || 0,
    eligible_period: policy.eligible_period || '',
    entitlement: policy.entitlement || [
      {
        layer: 1,
        amount: 0,
        start_year_of_service: 0,
        end_year_of_service: 99,
      },
    ],
    category: {
      gender: policy.category?.gender?.length
        ? policy.category.gender
        : [...Object.values(formikInitialValue.Gender)],
      marriage_status: policy.category?.marriage_status?.length
        ? [...policy.category.marriage_status.filter((item) => item.data !== 3)]
        : [...Object.values(formikInitialValue['Marriage Status'])],
      employment_type: policy.category?.employment_type?.length
        ? policy.category.employment_type
        : [...Object.values(formikInitialValue['Employment Type'])],
    },
    accrual_type: policy.accrual_type || null,
    accrual_happen: policy.accrual_happen || null,
    approval_route_id: policy.approval_route_id || '',
    quota_amount: policy.quota_amount || 0,
    quota_unit: policy.quota_unit || '',
    quota_category: policy.quota_category || '',
    restriction_amount: policy.restriction_amount || 0,
    day_prior: policy.day_prior || 0,
    carry_forward_amount: policy.carry_forward_amount || null,
    carry_forward_expiry_amount: policy.carry_forward_expiry_amount || 0,
    carry_forward_expiry_period: policy.carry_forward_expiry_period || '',
    deduct_from_leave_policy_id: policy.deduct_from_leave_policy_id || '',
    checkboxes: {
      quota: policy.quota_amount ? true : false,
      restriction: policy.restriction_amount ? true : false,
      minDate: policy.day_prior ? true : false,
      carryForward: policy.carry_forward_amount ? true : false,
      deductFromPolicy: policy.deduct_from_leave_policy_id ? true : false,
    },
  })
  return (
    <Box width="100%" minHeight="100vh" bgcolor="gray-background.main">
      <Formik
        initialValues={
          editMode && policy.id
            ? editModeInitialValues()
            : {
                name: '',
                description: '',
                attachment_required: false,
                reason_required: false,
                credit_deduction: false,
                half_day_option: false,
                include_non_working_days: false,
                cycle_period: '',
                eligible_amount: 0,
                eligible_period: '',
                entitlement: [
                  {
                    layer: 1,
                    amount: 0,
                    start_year_of_service: 0,
                    end_year_of_service: 99,
                  },
                ],
                category: {
                  gender: [...Object.values(formikInitialValue.Gender)],
                  marriage_status: [...Object.values(formikInitialValue['Marriage Status'])],
                  employment_type: [...Object.values(formikInitialValue['Employment Type'])],
                },
                accrual_type: null,
                accrual_happen: null,
                approval_route_id: '',
                quota_amount: 0,
                quota_unit: '',
                quota_category: '',
                restriction_amount: 0,
                day_prior: 0,
                carry_forward_amount: null,
                carry_forward_expiry_amount: 0,
                carry_forward_expiry_period: '',
                deduct_from_leave_policy_id: '',
                checkboxes: {
                  quota: false,
                  restriction: false,
                  minDate: false,
                  carryForward: false,
                  deductFromPolicy: false,
                },
              }
        }
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={true}
        onSubmit={async (values) => {
          if (values.cycle_period === 'yearly') {
            let { checkboxes, ...data } = values

            data = {
              ...data,
              category: editMode
                ? data.category
                : {
                    ...data.category,
                    marriage_status: [
                      ...data.category.marriage_status,
                      {
                        data: 3,
                        status: data.category.marriage_status.find((el) => el.data === 2).status,
                      },
                    ],
                  },
              type: 1,
              attachment_required: data.attachment_required ? 1 : 0,
              reason_required: data.reason_required ? 1 : 0,
              half_day_option: data.half_day_option ? 1 : 0,
              include_non_working_days: data.include_non_working_days ? 1 : 0,
              eligible_amount: data.eligible_amount ? data.eligible_amount : null,
              eligible_period: data.eligible_period ? data.eligible_period : null,
              quota_amount: checkboxes.quota ? data.quota_amount : null,
              quota_unit: checkboxes.quota ? data.quota_unit : null,
              quota_category: checkboxes.quota ? data.quota_category : null,
              restriction_amount: checkboxes.restriction ? data.restriction_amount : null,
              day_prior: checkboxes.minDate ? data.day_prior : null,
              carry_forward_amount: checkboxes.carryForward ? data.carry_forward_amount : null,
              carry_forward_expiry_amount: checkboxes.carryForward
                ? data.carry_forward_expiry_amount
                : null,
              carry_forward_expiry_period: checkboxes.carryForward
                ? data.carry_forward_expiry_period
                : null,
              credit_deduction: data.credit_deduction ? 1 : 0,
              deduct_from_leave_policy_id: checkboxes.deductFromPolicy
                ? data.deduct_from_leave_policy_id
                : null,
              ...(policy?.status === 3 && editMode && { status: 1 }),
            }

            if (editMode) {
              mutationEdit.mutate(
                { id: policy.id, data, type: policy.type },
                {
                  onSuccess: () => navigate(-1),
                }
              )
            } else {
              mutation.mutate(data, {
                onSuccess: () => navigate(-1),
              })
            }
          }

          if (values.cycle_period === 'monthly') {
            let {
              checkboxes,
              carry_forward_amount,
              carry_forward_expiry_period,
              credit_deduction,
              ...data
            } = values

            data = {
              ...data,
              category: editMode
                ? data.category
                : {
                    ...data.category,
                    marriage_status: [
                      ...data.category.marriage_status,
                      {
                        data: 3,
                        status: data.category.marriage_status.find((el) => el.data === 2).status,
                      },
                    ],
                  },
              type: 1,
              attachment_required: data.attachment_required ? 1 : 0,
              reason_required: data.reason_required ? 1 : 0,
              half_day_option: data.half_day_option ? 1 : 0,
              include_non_working_days: data.include_non_working_days ? 1 : 0,
              eligible_amount: data.eligible_amount ? data.eligible_amount : null,
              eligible_period: data.eligible_period ? data.eligible_period : null,
              quota_amount: checkboxes.quota ? data.quota_amount : null,
              quota_unit: checkboxes.quota ? data.quota_unit : null,
              quota_category: checkboxes.quota ? data.quota_category : null,
              restriction_amount: checkboxes.restriction ? data.restriction_amount : null,
              day_prior: checkboxes.minDate ? data.day_prior : null,
              credit_deduction: 0,
              deduct_from_leave_policy_id: checkboxes.deductFromPolicy
                ? data.deduct_from_leave_policy_id
                : null,
              ...(policy?.status === 3 && editMode && { status: 1 }),
            }
            if (editMode) {
              mutationEdit.mutate(
                { id: policy.id, data, type: policy.type },
                {
                  onSuccess: () => navigate(-1),
                }
              )
            } else {
              mutation.mutate(data, {
                onSuccess: () => navigate(-1),
              })
            }
          }
        }}
      >
        {(formikBag) => (
          <Form>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={2}
              position={'sticky'}
              top={0}
              left={0}
              width={'100%'}
              borderBottom={'1px solid'}
              borderColor={'grayscale.line-light'}
            >
              <Typography
                variant="h4"
                color="dark-text.main"
                sx={{
                  fontWeight: 'bold',
                }}
              >
                {editMode
                  ? `Edit Leave Policy ${policy.id}`
                  : `Create new leave policy - with entitlement`}
              </Typography>
              <IconButton onClick={() => navigate(-1)}>
                <Icon>close</Icon>
              </IconButton>
            </Box>
            <Box
              display={'flex'}
              width={1}
              maxWidth={'1000px'}
              alignSelf={'center'}
              justifySelf={'center'}
              bgcolor={'background.main'}
            >
              <AdxTabsScrollable
                sxPanel={{ height: 'calc(100vh - 216px)' }}
                sxTabs={{ borderBottom: 1, borderColor: 'gray-line.main' }}
              >
                {createLeaveTabs.map((tab, index, arr) => (
                  <AdxTabsPanel
                    key={tab.number}
                    label={tab.label}
                    sx={{ p: 2, borderTop: index > 0 ? 1 : 0, borderColor: 'gray-line.main' }}
                  >
                    <Box display="flex" flexDirection="column" rowGap={1} py={2} width={1}>
                      <Typography variant="h4" color="dark-text.main" sx={{ fontWeight: 'bold' }}>
                        {tab.title}
                      </Typography>
                      <Typography variant="body1" color="light-gray-text.main">
                        {tab.subtitle}
                      </Typography>
                      {tab.forms && tab.forms({ formikBag, editMode, policy })}
                      {arr.length - 1 === index && (
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="flex-end"
                          justifyContent="center"
                          gap={2}
                        >
                          {Object.keys(formikBag.errors).length > 0 &&
                            formikBag.submitCount > 0 && (
                              <Typography color="error.main" variant="subtitle2">
                                Please resolve all the errors from this page
                              </Typography>
                            )}
                        </Box>
                      )}
                    </Box>
                  </AdxTabsPanel>
                ))}
              </AdxTabsScrollable>
            </Box>
            <Box
              position="fixed"
              bottom={0}
              left={0}
              display="flex"
              gap={1}
              width={'100%'}
              alignItems={'center'}
              justifyContent={'end'}
              py={3}
              px={2}
              bgcolor={'primary.light'}
              borderTop={'1px solid'}
              borderColor={'grayscale.line-light'}
            >
              <AdxButton variant={'outlined'} onClick={() => navigate(-1)} sx={{ width: '200px' }}>
                Cancel
              </AdxButton>
              <AdxButton
                variant={'contained'}
                onClick={() => formikBag.submitForm()}
                isLoading={mutation.isLoading || mutationEdit.isLoading}
                disabled={mutation.isLoading || mutationEdit.isLoading}
                sx={{ width: '300px' }}
              >
                {editMode ? `Save Policy` : `Create policy`}
              </AdxButton>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  )
}

export default CreateLeavePolicyEntitlement
